import { useEffect, useRef, useState } from 'react';
import LandingNav from '../components/landing/LandingNav';
import LandingFooter from '../components/landing/LandingFooter';
import GuideHero from '../components/guide/GuideHero';
import GuideSearch from '../components/guide/GuideSearch';
import GuideContents from '../components/guide/GuideContents';
import GettingStarted from '../components/guide/GettingStarted';
import FeatureGuide from '../components/guide/FeatureGuide';
import CreditsGuide from '../components/guide/CreditsGuide';
import GuideOutro from '../components/guide/GuideOutro';
import styles from './GuidePage.module.css';

// Height of the sticky nav. Used to hand the search over at the moment the hero copy tucks
// underneath it, rather than when it clears the bottom of the window.
const NAV_HEIGHT = 64;

// Public, self-serve product guide. Explains every RepBuddy feature end to end and how to
// use each one, so a customer can onboard without a founder walkthrough. Reuses the
// marketing nav/footer and owns its own scroll container (global `body { overflow: hidden }`
// keeps the app shell from scrolling).
export default function GuidePage() {
  // This div is the scroll container (not the window), so scroll state is read from it and
  // the back-to-top button scrolls it, not the document.
  const pageRef = useRef(null);
  const heroSearchRef = useRef(null);
  const [showTop, setShowTop] = useState(false);
  // Search lives in the hero on arrival and moves into the sticky nav once the hero copy is
  // gone, so it is reachable from anywhere on a page this long without costing a second
  // permanent bar of chrome.
  const [searchInNav, setSearchInNav] = useState(false);

  useEffect(() => {
    pageRef.current?.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const el = heroSearchRef.current;
    const root = pageRef.current;
    if (!el || !root || typeof IntersectionObserver === 'undefined') return undefined;

    // Root is the page's own scroll container, not the window. The negative top margin pulls
    // the root's top edge down past the sticky nav, so the handoff fires exactly as the hero
    // search slides behind it instead of once it is already hidden.
    const obs = new IntersectionObserver(
      ([e]) => setSearchInNav(!e.isIntersecting),
      { root, rootMargin: `-${NAV_HEIGHT}px 0px 0px 0px`, threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Reveal the back-to-top control once the reader is a screenful or so down the page.
  function handleScroll(e) {
    setShowTop(e.currentTarget.scrollTop > 700);
  }

  function scrollToTop() {
    pageRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div id="top" ref={pageRef} className={styles.page} onScroll={handleScroll}>
      <LandingNav
        fixed
        searchSlot={
          searchInNav ? (
            <GuideSearch
              idPrefix="guide-search-nav"
              compact
              hotkey
              scrollRoot={pageRef}
            />
          ) : null
        }
      />
      <main>
        <GuideHero searchRef={heroSearchRef} hasHotkey={!searchInNav} />
        <GuideContents />
        <GettingStarted />
        <FeatureGuide />
        <CreditsGuide />
        <GuideOutro />
      </main>
      <LandingFooter />

      <button
        type="button"
        className={`${styles.toTop} ${showTop ? styles.toTopShow : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
        tabIndex={showTop ? 0 : -1}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 19V5" />
          <path d="m6 11 6-6 6 6" />
        </svg>
      </button>
    </div>
  );
}
